// Lightweight synthesized ringtones (no audio assets needed).
let ctx;
const getCtx = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!ctx && AC) ctx = new AC();
  return ctx;
};

export const RINGTONES = [
  { id: "royal", label: "Royal", notes: [[880, 0], [1320, 0.12]] },
  { id: "chime", label: "Chime", notes: [[660, 0], [990, 0.1], [1320, 0.2]] },
  { id: "ping", label: "Ping", notes: [[1200, 0]] },
  { id: "soft", label: "Soft", notes: [[520, 0], [440, 0.16]] },
  { id: "elegant", label: "Elegant", src: "/sounds/elegant.mp3" },
  { id: "notify1", label: "Notify 1", src: "/sounds/notify-1.mp3" },
  { id: "notify2", label: "Notify 2", src: "/sounds/notify-2.mp3" },
  { id: "none", label: "None", notes: [] },
];

// Shared <audio> element for file-based ringtones (reused so previews don't stack up).
let fileAudio;
function playFile(src) {
  try {
    if (!fileAudio) fileAudio = new Audio();
    fileAudio.pause();
    fileAudio.src = src;
    fileAudio.currentTime = 0;
    const p = fileAudio.play();
    if (p && p.catch) p.catch(() => {});
  } catch (e) {}
}

export function playTone(id) {
  const rt = RINGTONES.find((r) => r.id === id);
  if (!rt) return;
  if (rt.src) { playFile(rt.src); return; }
  if (!rt.notes || !rt.notes.length) return;
  const c = getCtx();
  if (!c) return;
  try {
    if (c.state === "suspended") c.resume();
    rt.notes.forEach(([freq, delay]) => {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      o.connect(g); g.connect(c.destination);
      const t = c.currentTime + delay;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      o.start(t); o.stop(t + 0.4);
    });
  } catch (e) {}
}

export const getSentTone = () => localStorage.getItem("rc_tone_sent") || "ping";
export const getRecvTone = () => localStorage.getItem("rc_tone_recv") || "royal";
export const getCallTone = () => localStorage.getItem("rc_tone_call") || "chime";
export const soundsEnabled = () => localStorage.getItem("rc_sounds") !== "0"; // default on

export const playSent = () => _playCategory("sent_message");
export const playRecv = () => _playCategory("new_message");
export const playCall = () => soundsEnabled() && playTone(getCallTone());

// Soft ringback tone for the caller while waiting for the other side to answer.
export function playRingback() {
  if (!soundsEnabled()) return;
  const c = getCtx();
  if (!c) return;
  try {
    if (c.state === "suspended") c.resume();
    [0, 0.35].forEach((delay) => {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = "sine";
      o.frequency.value = 440;
      o.connect(g); g.connect(c.destination);
      const t = c.currentTime + delay;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.12, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      o.start(t); o.stop(t + 0.35);
    });
  } catch (e) {}
}

// Caller-side ringback: loops the uploaded ringtone for the person placing the
// call while it rings, and stops the instant the other side answers or the call
// ends (stopRingback). Used for both outgoing audio AND video calls.
let _ringbackAudio = null;
export function startRingback() {
  try {
    stopRingback();
    if (!soundsEnabled()) return;
    _ringbackAudio = new Audio("/sounds/ringback.mp3");
    _ringbackAudio.loop = true;
    _ringbackAudio.volume = 0.7;
    const p = _ringbackAudio.play();
    if (p && p.catch) p.catch(() => {});
  } catch (e) {}
}
export function stopRingback() {
  if (_ringbackAudio) {
    try { _ringbackAudio.pause(); _ringbackAudio.currentTime = 0; } catch (e) {}
    _ringbackAudio = null;
  }
}

// ---------------------------------------------------------------------------
// Admin-managed ringtones (server files) — chosen per category by each user.
// Falls back to the built-in tones above when nothing is selected/uploaded.
// ---------------------------------------------------------------------------
import api from "@/lib/api";
import { fileUrl } from "@/lib/uploads";

export const RINGTONE_CATEGORIES = [
  { key: "incoming_audio", label: "Incoming Call (Audio)", labelFr: "Appel entrant (Audio)" },
  { key: "incoming_video", label: "Incoming Call (Video)", labelFr: "Appel entrant (Vidéo)" },
  { key: "new_message", label: "New Message", labelFr: "Nouveau message" },
  { key: "sent_message", label: "Sent Message", labelFr: "Message envoyé" },
  { key: "general", label: "General", labelFr: "Général" },
];

let _ringCache = [];
let _sel = (() => { try { return JSON.parse(localStorage.getItem("rc_ringtones") || "{}"); } catch { return {}; } })();
// One-time cleanup: the old per-tone picker (rc_tone_*) was removed; purge leftovers
// so they never override the new default/selection logic.
try { ["rc_tone_sent", "rc_tone_recv", "rc_tone_call"].forEach((k) => localStorage.removeItem(k)); } catch {}

export const cachedRingtones = () => _ringCache;
export const ringSelection = () => _sel;

export async function loadRingtones(force) {
  if (_ringCache.length && !force) return _ringCache;
  try { const { data } = await api.get("/ringtones"); _ringCache = Array.isArray(data) ? data : []; } catch (e) {}
  return _ringCache;
}

export function syncSelectionFromUser(user) {
  if (user && user.ringtones && typeof user.ringtones === "object") {
    _sel = { ..._sel, ...user.ringtones };
    try { localStorage.setItem("rc_ringtones", JSON.stringify(_sel)); } catch {}
  }
}

export async function setRingSelection(category, ringtoneId) {
  _sel = { ..._sel };
  if (ringtoneId) _sel[category] = ringtoneId; else delete _sel[category];
  try { localStorage.setItem("rc_ringtones", JSON.stringify(_sel)); } catch {}
  try { await api.put("/me/ringtones", { category, ringtone_id: ringtoneId || null }); } catch (e) {}
}

// Fixed, app-wide default ringtones per category. Intentionally does NOT read the
// old `rc_tone_*` keys — those were the removed per-tone picker and must not leak in
// as the "default". Priority in resolveTone: user's pick → admin default → these.
const BUILTIN_DEFAULT = {
  incoming_audio: "elegant",
  incoming_video: "elegant",
  new_message: "notify2",
  sent_message: "notify1",
  general: "elegant",
};
const _builtinFor = (category) => BUILTIN_DEFAULT[category] || "royal";

// Effective ringtone for a category → { kind:'file', src, ringtone } | { kind:'builtin', id }
export function resolveTone(category) {
  const chosenId = _sel[category];
  let rt = chosenId && _ringCache.find((r) => r.id === chosenId && r.category === category);
  if (!rt) rt = _ringCache.find((r) => r.category === category && r.is_default);
  if (rt) return { kind: "file", src: fileUrl(rt.url), ringtone: rt };
  return { kind: "builtin", id: _builtinFor(category) };
}

let _once;
function _playOnceFile(src) {
  try {
    if (!_once) _once = new Audio();
    _once.loop = false; _once.pause(); _once.src = src; _once.currentTime = 0;
    const p = _once.play(); if (p && p.catch) p.catch(() => {});
  } catch (e) {}
}
function _playCategory(category) {
  if (!soundsEnabled()) return;
  const tone = resolveTone(category);
  if (tone.kind === "file") _playOnceFile(tone.src); else playTone(tone.id);
}

// Looping call ringtone (true loop for uploaded files; re-triggered synth for built-ins).
let _callAudio = null, _callInterval = null;
export function startCallRing(isVideo) {
  try {
    stopCallRing();
    if (!soundsEnabled()) return;
    const tone = resolveTone(isVideo ? "incoming_video" : "incoming_audio");
    if (tone.kind === "file") {
      try {
        _callAudio = new Audio(tone.src);
        _callAudio.loop = true;
        const p = _callAudio.play(); if (p && p.catch) p.catch(() => {});
      } catch (e) {}
    } else {
      // Built-in default: loop the audio file if it's file-based (e.g. "elegant"),
      // otherwise re-trigger the short synth tone on an interval.
      const rt = RINGTONES.find((r) => r.id === tone.id);
      if (rt && rt.src) {
        try {
          _callAudio = new Audio(rt.src);
          _callAudio.loop = true;
          const p = _callAudio.play(); if (p && p.catch) p.catch(() => {});
        } catch (e) {}
      } else {
        playTone(tone.id);
        _callInterval = setInterval(() => playTone(tone.id), 2500);
      }
    }
  } catch (e) {}
}
export function stopCallRing() {
  if (_callInterval) { clearInterval(_callInterval); _callInterval = null; }
  if (_callAudio) { try { _callAudio.pause(); _callAudio.currentTime = 0; } catch (e) {} _callAudio = null; }
}

// Inline preview player (admin manager + user picker). Returns nothing; use stopPreview to halt.
let _preview = null;
export function previewSrc(src) {
  stopPreview();
  try { _preview = new Audio(src); _preview.loop = false; const p = _preview.play(); if (p && p.catch) p.catch(() => {}); } catch (e) {}
  return _preview;
}
export function stopPreview() { if (_preview) { try { _preview.pause(); } catch (e) {} _preview = null; } }
export function isPreviewing() { return !!_preview && !_preview.paused; }
